/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   2/28/22
 */

import React from "react";
import MonthCalendar from "./MonthCalendar";
import Toolbar from "./Toolbar";
import {Paper, Stack} from "@mui/material";
import { areIntervalsOverlapping, differenceInWeeks, startOfToday } from "date-fns";
import DayCalendar from "./DayCalendar";
import {useSelector} from "react-redux";
import {State} from "../state/store";
import {useQuery, useSubscription} from "urql";
import {EmployeesFilterQuery, EmployeesFilterResponse, EmployeesFilterVars} from "../graphql/queries/employees";
import {
	EmployeesSubscription,
	EmployeesSubscriptionResponse,
	EmployeesSubscriptionVars
} from "../graphql/subscriptions/employees";
import {
	CalendarEventsQuery, CalendarEventsQueryVars,
	CalendarEventsResponse
} from "../graphql/queries/calendar-events";
import {
	CalendarEventsSubscription,
	CalendarEventsSubscriptionResponse,
	CalendarEventsSubscriptionVars
} from "../graphql/subscriptions/calendar-events";
import LoadingHero from "../components/LoadingHero";
import {CalendarView as CalendarViewSetting, Employee} from "../interfaces/Global";
import {HolidaysQuery, HolidaysQueryVars, HolidaysResponse} from "../graphql/queries/holidays";
import HeroMessage from "../components/HeroMessage";
import {WorkdayEventsQuery, WorkdayEventsQueryVars, WorkdayEventsResponse} from "../graphql/queries/workday-events";
import {getEventType} from "./event-types";
import { OpsGenieEventsQuery, OpsGenieEventsQueryVars, OpsGenieEventsResponse } from "../graphql/queries/opsgenie";

export default function DataContainer() {

	const defaultView = useSelector((s:State) => s.settings.defaultView || "month");
	const [day, setDay] = React.useState(startOfToday());
	const [view, setView] = React.useState(defaultView);
	const filter = useSelector((s:State) => s.employeesFilter);

	const [ employeesResult ] = useQuery<EmployeesFilterResponse, EmployeesFilterVars>({
		query: EmployeesFilterQuery,
		variables: { filter },
	});

	const employees = React.useMemo(() =>
		(employeesResult?.data?.employeesFilter.items||[])
			.sort((a,b) => (a.firstName||0) < (b.firstName||0) ? -1:1),
		[employeesResult]);

	return (
		<Stack>
			<Toolbar
				employees={employees}
				day={day} setDay={setDay}
				view={view} setView={setView}
			/>
			{employeesResult.error &&
				<HeroMessage
					title={"Request Error"}
					message={employeesResult.error?.message}
				/>
			}
			{employeesResult.fetching &&
				<LoadingHero />}
			{!employeesResult.error && !employeesResult.fetching && employees.length === 0 &&
				<HeroMessage
					title={"No Employees"}
					message={"Employee filter didn't return any results"}
				/>}
			{!employeesResult.error && !employeesResult.fetching && employees.length > 0 &&
				<CalendarView
					day={day}
					view={view}
					employees={employees}
				/>}
		</Stack>
	);

}

function CalendarView({ employees, view, day }: { employees: Employee[], view: CalendarViewSetting, day: Date }) {

	const range = useSelector((s:State) => s.range);
	const displayEventTypes = useSelector((s:State) => s.settings.displayEventTypes);
	const displayGoogleEvents = useSelector((s:State) => s.settings.displayGoogleEvents);
	const displayVegaEvents = useSelector((s:State) => s.settings.displayVegaEvents);
	const displayHolidays = useSelector((s:State) => s.settings.displayHolidays);
	const displayWorkdayEvents = useSelector((s:State) => s.settings.displayWorkdayEvents);
	const displayOpsGenieEvents = useSelector((s:State) => s.settings.displayOpsGenieEvents);

	const googleEvents = useSelector((s:State) => displayGoogleEvents ? s.googleEvents.filter(e => {
		if (!displayEventTypes || displayEventTypes.length === 0) return true;
		const type = getEventType(e.typeId);
		return displayEventTypes.includes(type?.parentId || "");
	}) : []);

	useSubscription<EmployeesSubscriptionResponse, any, EmployeesSubscriptionVars>({
		query: EmployeesSubscription,
		variables: {
			employees: employees.map(e => e.id),
		}
	});

	const [ calendarEventsResult ] = useQuery<CalendarEventsResponse, CalendarEventsQueryVars>({
		query: CalendarEventsQuery,
		variables: {
			employeeIdIn: employees.map(e => e.id),
			endDatetimeGt: new Date(range.start),
			startDatetimeLt: new Date(range.end),
			limit: 1000
		},
		requestPolicy: "cache-and-network",
		pause: !displayVegaEvents
	});

	const [ workdayEventsResult ] = useQuery<WorkdayEventsResponse, WorkdayEventsQueryVars>({
		query: WorkdayEventsQuery,
		variables: {
			employees: employees.map(e => e.id),
		},
		pause: !displayWorkdayEvents
	});

	const opsGenieSchedules = React.useMemo(() => {
		const schedules: string[] = [];
		employees.forEach(e => {
			e.properties?.opsGenieSchedules.forEach(s => {
				if (!schedules.includes(s)) {
					schedules.push(s);
				}
			})
		});
		return schedules;
	}, [employees]);

	const [ opsGenieEventsResult ] = useQuery<OpsGenieEventsResponse, OpsGenieEventsQueryVars>({
		query: OpsGenieEventsQuery,
		variables: {
			schedules: opsGenieSchedules,
			employees: employees.map(e => e.id),
			from: new Date(range.start).toISOString(),
			weeks: differenceInWeeks(new Date(range.end), new Date(range.start)) + 1
		},
		pause: !displayOpsGenieEvents || opsGenieSchedules.length === 0
	});

	const [ holidaysResult ] = useQuery<HolidaysResponse, HolidaysQueryVars>({
		query: HolidaysQuery,
		variables: {
			countryCodes: employees.map(e => e.countryCode).filter(v => v)
				.filter((v, i, a) => a.indexOf(v) === i) as string[],
			from: new Date(range.start),
			to: new Date(range.end)
		},
		pause: !displayHolidays
	});

	useSubscription<CalendarEventsSubscriptionResponse, any, CalendarEventsSubscriptionVars>({
		query: CalendarEventsSubscription,
		variables: {
			employees: employees.map(e => e.id),
			from: new Date(range.start),
			to: new Date(range.end),
		},
		pause: !displayVegaEvents
	});

	const vegaEvents = React.useMemo(
		() => {
			if (!displayVegaEvents) return [];
			return  (calendarEventsResult?.data?.calendarEvents.items||[])
				.filter(e => areIntervalsOverlapping({ start: e.startDatetime, end: e.endDatetime }, range))
				.filter(e => {
					if (!displayEventTypes || displayEventTypes.length === 0) return true;
					const type = getEventType(e.typeId);
					return displayEventTypes.includes(type?.parentId || "");
				})
		},
		[calendarEventsResult, range, displayVegaEvents, displayEventTypes]
	);

	const holidays = React.useMemo(
		() => displayHolidays ? (holidaysResult?.data?.holidays.items||[])
			.filter(e => areIntervalsOverlapping({ start: e.startDate, end: e.endDate }, range)) : [],
		[holidaysResult, range, displayHolidays]
	);

	const workdayEvents = React.useMemo(
		() => {
			if (!displayWorkdayEvents || (displayEventTypes && displayEventTypes.length > 0 && !displayEventTypes.includes("PTO"))) {
				return [];
			}
			return  (workdayEventsResult?.data?.workdayEvents.items||[])
				.filter(e => areIntervalsOverlapping({ start: e.startDatetime, end: e.endDatetime }, range));
		},
		[displayWorkdayEvents, displayEventTypes, workdayEventsResult?.data?.workdayEvents.items, range]
	);

	const opsGenieEvents = React.useMemo(
		() => {
			if (!displayOpsGenieEvents || (displayEventTypes && displayEventTypes.length > 0 && !displayEventTypes.includes("A"))) {
				return [];
			}
			return  (opsGenieEventsResult?.data?.opsGenieEvents.items||[])
				.filter(e => areIntervalsOverlapping({ start: e.startDatetime, end: e.endDatetime }, range));
		},
		[displayEventTypes, displayOpsGenieEvents, opsGenieEventsResult?.data?.opsGenieEvents.items, range]
	);

	return (
		<Paper>
			{view === "month" &&
				<MonthCalendar
					employees={employees}
					vegaEvents={vegaEvents}
					googleEvents={googleEvents}
					workdayEvents={workdayEvents}
					opsGenieEvents={opsGenieEvents}
					holidays={holidays}
				/>}
			{view === "day" &&
				<DayCalendar
					day={day}
					employees={employees}
					vegaEvents={vegaEvents}
					googleEvents={googleEvents}
					workdayEvents={workdayEvents}
					opsGenieEvents={opsGenieEvents}
					holidays={holidays}
				/>}
		</Paper>
	);
}